import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, CircleHelp } from "lucide-react";

const RATING = { satisfactory: "bg-emerald-50 text-emerald-800 border-emerald-300", conditional: "bg-amber-50 text-amber-900 border-amber-300", unsatisfactory: "bg-rose-50 text-rose-800 border-rose-300" };
const STATUS = { pass: ["text-emerald-700", CheckCircle2], fail: ["text-rose-700", XCircle], missing: ["text-amber-700", CircleHelp] };

export function RatingBadge({ rating, size = "sm" }) {
  if (!rating) return <Badge variant="outline" className="text-slate-400 font-sans">n/a</Badge>;
  return <Badge className={`border font-sans ${RATING[rating]} ${size === "lg" ? "text-base px-3 py-1" : ""}`} data-testid={`rating-${rating}`}>{rating}</Badge>;
}

function CheckRow({ c }) {
  const [tone, Icon] = STATUS[c.status];
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0 text-xs" data-testid={`audit-check-${c.status}`}>
      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${tone}`} />
      <div className="flex-1 min-w-0"><span className="font-medium text-slate-900">{c.title}</span>{c.severity === "acute" && c.status !== "pass" && <Badge className="ml-1 bg-rose-600 text-white border-0 text-[10px] py-0">ACUTE</Badge>}<div className="text-slate-600">{c.detail}</div></div>
      <span className="font-mono text-[10px] text-slate-400 shrink-0">{c.cite}</span>
    </div>
  );
}

export function AuditDetail({ audit }) {
  const [factor, setFactor] = useState(null);
  const shown = audit.checks.filter((c) => !factor || c.factor === factor);
  return (
    <div className="space-y-4" data-testid="audit-detail">
      <div className="flex items-center gap-4 flex-wrap rounded-md border border-slate-200 bg-slate-50 p-4">
        <div><div className="tag-mono text-slate-500">OVERALL PROPOSED RATING</div><div className="mt-1"><RatingBadge rating={audit.overall} size="lg" /></div></div>
        <div className="text-sm text-slate-700 font-mono">{audit.counts.pass} pass · <span className="text-rose-700">{audit.counts.fail} fail</span> · <span className="text-amber-700">{audit.counts.missing} missing</span></div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {audit.factors.map((f) => (
          <button type="button" key={f.key} onClick={() => setFactor(factor === f.key ? null : f.key)} className={`text-left rounded-md border p-3 transition-colors ${factor === f.key ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`audit-factor-${f.key}`}>
            <div className="text-[11px] font-mono text-slate-500 truncate">{f.label}</div>
            <div className="mt-1 flex items-center justify-between gap-1"><RatingBadge rating={f.rating} /><span className="text-xs font-mono text-slate-600">{f.total ? `${f.pass}/${f.total}` : "—"}</span></div>
          </button>
        ))}
      </div>
      {audit.fixes.length > 0 && !factor && (
        <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3" data-testid="audit-fixes">
          <div className="text-sm font-semibold text-rose-900 mb-1">Corrective actions ({audit.fixes.length})</div>
          {audit.fixes.slice(0, 12).map((c, i) => <CheckRow key={`${c.factor}-${c.title}-${c.subject || ""}-${i}`} c={c} />)}
          {audit.fixes.length > 12 && <div className="text-xs text-slate-500 mt-1">+{audit.fixes.length - 12} more in the PDF report and factor views.</div>}
        </div>
      )}
      <div>
        <div className="text-sm font-semibold text-slate-900 mb-1">{factor ? audit.factors.find((f) => f.key === factor)?.label : "All checks"} <span className="text-xs text-slate-500 font-normal">({shown.length})</span></div>
        <div className="max-h-[420px] overflow-y-auto pr-1">{shown.map((c, i) => <CheckRow key={`${c.factor}-${c.title}-${c.subject || ""}-${i}`} c={c} />)}</div>
      </div>
    </div>
  );
}
